import { type ModalWidth } from "components/modal";
import React, { useRef } from "react";
import { Modal } from "components";
import { type RemovableProps } from "components/Removables/Removable";
import { ModalPickerProps } from "./props";
import { useValidateIsNotNull } from "formvalidation/hooks";
import { SelectOrNew } from "./SelectOrNew";

interface Props<T> extends ModalPickerProps<T> {
  buttonLabel: string
  modalTitle: string
  modalSize: ModalWidth
  modalHeight?: number

  onNewClicked: () => void

  renderModalContent: (closeModal: () => void) => JSX.Element
  renderRemovable: (props: RemovableProps<T>) => JSX.Element
}

const ModalPickerWithChooseButton = <T extends unknown>(props: Props<T>) => {
  const { canCreateNew = false } = props;

  const [isModalOpen, setIsModalOpen] = React.useState(props.startOpen ?? false);
  const [modalJustClosed, setModalJustClosed] = React.useState(false);

  const onChange = (newValue: T) => {
    props.onChange(newValue);
    if (newValue == null && props.required) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalJustClosed(true);
    setTimeout(() => {
      setModalJustClosed(false);
    }, 2500);
  };

  const hasValue = Boolean(props.value);

  const ref = useRef<HTMLDivElement>(null);

  let hasError: boolean = false;

  if (props.legacyHasError != null) {
    hasError = props.legacyHasError;
  }
  else if (props.required && props.legacyHasError == null) {
    const validation = useValidateIsNotNull({
      key: props.validatorKey,
      title: props.validatorKey,
      value: props.value,
      ref
    });

    hasError = validation.error.hasError;
  }

  return <>
    <div ref={ref}></div>
    {!hasValue && <div className="flex">
      <SelectOrNew
        canCreateNew={canCreateNew}
        newButton={{
          label: RESX.Buttons.btnNew,
          onClick: props.onNewClicked
        }}
        selectButton={{
          label: props.buttonLabel,
          onClick: () => { setIsModalOpen(true); }
        }}
        hasError={hasError}
      />
    </div>}

    {hasValue && props.renderRemovable({ autoFocus: modalJustClosed, value: props.value, onChange, readonly: false, error: hasError })}

    <Modal
      isOpen={isModalOpen}
      handleClose={closeModal}
      width={props.modalSize}
      gap="regular"
      title={props.modalTitle}
      height={props.modalHeight ?? 500}
    >
      {props.renderModalContent(closeModal)}
    </Modal>
  </>;
};

export default ModalPickerWithChooseButton;